import produce from "immer";
import { orderBy } from "lodash";
import { Action } from "redux";
import {
  ICommentToDelete,
  ICommentToUpdate,
  IMeasuringDeviceAlert,
  ISensorComment,
  ISensorData,
  SensorWithAlarm,
} from "./types";

export interface ISafetySensorState {
  sensorComments: ISensorComment[];
  monitoringOn: boolean;
  allSensorDataForSingleNode: ISensorData[];
  sensorsWithAlerts: SensorWithAlarm[];
  isFetching: boolean;
}

export const initialState: ISafetySensorState = {
  isFetching: false,
  monitoringOn: false,
  sensorComments: [],
  allSensorDataForSingleNode: [],
  sensorsWithAlerts: [],
};

const FETCHING = "SENSOR/FETCHING";
const COMBINED_SENSOR_DATA = "SENSOR/COMBINED_SENSOR_DATA";
const FETCHING_MEASURINGDEVICEALERT =
  "SENSOR/MEASURINGDEVICE/FETCHING_MEASURINGDEVICEALERT";
const FETCHING_MEASURINGDEVICE_ERROR = "SENSOR/MEASURINGDEVICE/FETCHING_ERROR";
const FETCHING_SENSORCOMMENTS = "SENSOR/SENSORCOMMENTS/FETCHING_SENSORCOMMENTS";
const FETCHING_SENSORCOMMENTS_ERROR = "SENSOR/FETCHING_SENSORCOMMENTS_ERROR";
const ADD_SENSORCOMMENT = "SENSOR/ADD_COMMENT";
const DELETE_SENSORCOMMENT = "SENSOR/DELETE_COMMENT";
const PUT_SENSORCOMMENT = "SENSOR/PUT_COMMENT";
const FETCHING_ERROR = "SENSOR/FETCHING_ERROR";
const FETCHING_CURRENT_ALARMS = "SENSOR/FETCHING_CURRENT_ALARMS";
const COMBINED_SENSOR_DATA_ERROR = "SENSOR/COMBINED_SENSOR_DATA_ERROR";
const TOGGLE_MONITORING_FOR_ALL_SENSORS =
  "SENSOR/TOGGLE_MONITORING_FOR_ALL_SENSORS";
const TOGGLE_MONITORING_FOR_SINGLE_SENSOR =
  "SENSOR/TOGGLE_MONITORING_FOR_SINGLE_SENSOR";
const FETCHING_SENSOR_HISTORY_SUCCESS =
  "SENSOR/FETCHING_SENSOR_HISTORY_SUCCESS";
export interface IFetchingAction {
  type: typeof FETCHING;
}
export interface IFetchingSensorHistorySuccessAction {
  type: typeof FETCHING_SENSOR_HISTORY_SUCCESS;
  payload: { results: ISensorData };
}

export interface ICombinedSensorDataAction {
  type: typeof COMBINED_SENSOR_DATA;
  payload: { results: ISensorData[] };
}
export interface ICombinedSensorDataFailedAction {
  type: typeof COMBINED_SENSOR_DATA_ERROR;
}
export interface ICurrentAlarmsAction {
  type: typeof FETCHING_CURRENT_ALARMS;
  payload: { results: SensorWithAlarm[] };
}

export interface IFetchingSensorCommentsAction {
  type: typeof FETCHING_SENSORCOMMENTS;
  payload: { results: ISensorComment[] };
}
export interface IFetchingSensorCommentsErrorAction {
  type: typeof FETCHING_SENSORCOMMENTS_ERROR;
}
export interface IFetchingMeasuringDeviceAlertAction {
  type: typeof FETCHING_MEASURINGDEVICEALERT;
  payload: IMeasuringDeviceAlert;
}
export interface IFetchingMeasuringDeviceAlertErrorAction {
  type: typeof FETCHING_MEASURINGDEVICE_ERROR;
}
export interface IFetchingErrorAction {
  type: typeof FETCHING_ERROR;
}
export interface IAddCommentAction {
  type: typeof ADD_SENSORCOMMENT;
  payload: ISensorComment;
}
export interface IDeleteCommentAction {
  type: typeof DELETE_SENSORCOMMENT;
  payload: ICommentToDelete;
}
export interface IPutCommentAction {
  type: typeof PUT_SENSORCOMMENT;
  payload: ICommentToUpdate;
}
export interface IToggleMonitoringForAllSensorsAction {
  type: typeof TOGGLE_MONITORING_FOR_ALL_SENSORS;
  payload: { toggleMonitoring: boolean };
}

export interface IToggleMonitoringForSingleSensorsAction {
  type: typeof TOGGLE_MONITORING_FOR_SINGLE_SENSOR;
  payload: { sensor: ISensorData };
}

export const actionCreator = {
  fetching: { type: FETCHING } as IFetchingAction,
  fetchingError: {
    type: FETCHING_ERROR,
  } as IFetchingErrorAction,
  fetchingSensorDataSuccess: (results: ISensorData[]) =>
    ({
      type: COMBINED_SENSOR_DATA,
      payload: { results },
    } as ICombinedSensorDataAction),
  fetchingCombinedSensorDataError: {
    type: COMBINED_SENSOR_DATA_ERROR,
  } as ICombinedSensorDataFailedAction,
  fetchingCurrentAlarmsSuccess: (results: SensorWithAlarm[]) =>
    ({
      type: FETCHING_CURRENT_ALARMS,
      payload: { results },
    } as ICurrentAlarmsAction),
  fetchingSensorHistorySuccess: (results: ISensorData) =>
    ({
      type: FETCHING_SENSOR_HISTORY_SUCCESS,
      payload: { results },
    } as IFetchingSensorHistorySuccessAction),
  fetchingMeasuringDeviceSuccess: (
    measuringDeviceAlert: IMeasuringDeviceAlert
  ) =>
    ({
      type: FETCHING_MEASURINGDEVICEALERT,
      payload: measuringDeviceAlert,
    } as IFetchingMeasuringDeviceAlertAction),

  fetchingSensorComments: (results: ISensorComment[]) =>
    ({
      type: FETCHING_SENSORCOMMENTS,
      payload: { results },
    } as IFetchingSensorCommentsAction),
  fetchingSensorCommentsError: {
    type: FETCHING_SENSORCOMMENTS_ERROR,
  } as IFetchingSensorCommentsErrorAction,
  addSensorCommentSuccess: (comment: ISensorComment) =>
    ({
      type: ADD_SENSORCOMMENT,
      payload: comment,
    } as IAddCommentAction),
  deletingSensorCommentSuccess: (commentId: ICommentToDelete) =>
    ({
      type: DELETE_SENSORCOMMENT,
      payload: commentId,
    } as IDeleteCommentAction),
  putSensorCommentSuccess: (commentToUpdate: ICommentToUpdate) =>
    ({
      type: PUT_SENSORCOMMENT,
      payload: commentToUpdate,
    } as IPutCommentAction),
  toggleMonitoringForAllSensors: (toggleMonitoring: boolean) =>
    ({
      type: TOGGLE_MONITORING_FOR_ALL_SENSORS,
      payload: { toggleMonitoring },
    } as IToggleMonitoringForAllSensorsAction),
  toggleMonitoringForSingleSensor: (sensor: ISensorData) =>
    ({
      type: TOGGLE_MONITORING_FOR_SINGLE_SENSOR,
      payload: { sensor },
    } as IToggleMonitoringForSingleSensorsAction),
};

export const sensorsReducer = (state = initialState, action: Action<string>) =>
  produce(state, (draft) => {
    switch (action.type) {
      case FETCHING:
        draft.isFetching = true;
        break;
      case COMBINED_SENSOR_DATA_ERROR:
        draft.isFetching = false;
        draft.allSensorDataForSingleNode =
          initialState.allSensorDataForSingleNode;
        break;

      case FETCHING_MEASURINGDEVICEALERT:
      case COMBINED_SENSOR_DATA:
        const combinedSensorDataAction = action as ICombinedSensorDataAction;
        draft.isFetching = false;
        draft.allSensorDataForSingleNode =
          combinedSensorDataAction.payload.results;

        if (draft.allSensorDataForSingleNode.some((m) => m.Monitoring)) {
          draft.monitoringOn = true;
          break;
        }

        if (draft.allSensorDataForSingleNode.some((m) => !m.Monitoring)) {
          draft.monitoringOn = false;
          break;
        }
        break;

      case FETCHING_CURRENT_ALARMS:
        const currentAlarmsAction = action as ICurrentAlarmsAction;
        draft.isFetching = false;
        draft.sensorsWithAlerts = currentAlarmsAction.payload.results;
        break;

      case FETCHING_SENSORCOMMENTS:
        const fetchingSensorCommentsAction =
          action as IFetchingSensorCommentsAction;
        draft.isFetching = false;
        draft.sensorComments = fetchingSensorCommentsAction.payload.results;
        break;
      case FETCHING_SENSORCOMMENTS_ERROR:
        draft.isFetching = false;
        draft.sensorComments = initialState.sensorComments;
        break;
      case ADD_SENSORCOMMENT:
        const addCommentAction = action as IAddCommentAction;
        draft.isFetching = false;
        draft.sensorComments.push(addCommentAction.payload);
        draft.sensorComments = orderBy(
          draft.sensorComments,
          ["Created"],
          ["desc"]
        );
        break;

      case DELETE_SENSORCOMMENT:
        const deleteCommentAction = action as IDeleteCommentAction;
        draft.isFetching = false;
        draft.sensorComments = draft.sensorComments?.filter(
          (s) => s.ID !== deleteCommentAction.payload.id
        );
        break;

      case PUT_SENSORCOMMENT:
        const putCommentAction = action as IPutCommentAction;
        draft.isFetching = false;
        const index = state.sensorComments.findIndex(
          (s) => s.ID === putCommentAction.payload.Id
        );
        draft.sensorComments[index].Text = putCommentAction.payload.Text;
        break;

      case FETCHING_ERROR:
        draft.isFetching = false;
        draft.sensorsWithAlerts = initialState.sensorsWithAlerts;
        break;

      case FETCHING_SENSOR_HISTORY_SUCCESS:
        const fetchHistoryAction =
          action as IFetchingSensorHistorySuccessAction;
        draft.allSensorDataForSingleNode = [
          ...draft.allSensorDataForSingleNode.filter(
            (sensor) =>
              sensor.MeasuringPointID !==
              fetchHistoryAction.payload.results.MeasuringPointID
          ),
          fetchHistoryAction.payload.results,
        ];
        break;
      case TOGGLE_MONITORING_FOR_ALL_SENSORS:
        const toggleMonitoringAction =
          action as IToggleMonitoringForAllSensorsAction;
        const { toggleMonitoring } = toggleMonitoringAction.payload;

        draft.monitoringOn = toggleMonitoring;
        draft.allSensorDataForSingleNode = draft.allSensorDataForSingleNode.map(
          (s) => ({
            ...s,
            Monitoring: toggleMonitoring,
          })
        );
        draft.isFetching = false;
        break;
      case TOGGLE_MONITORING_FOR_SINGLE_SENSOR:
        const toggleSingleSensor =
          action as IToggleMonitoringForSingleSensorsAction;
        const { sensor } = toggleSingleSensor.payload;

        draft.allSensorDataForSingleNode = draft.allSensorDataForSingleNode.map(
          (s) =>
            s.MeasuringPointID === sensor.MeasuringPointID
              ? { ...s, Monitoring: !!sensor.Monitoring }
              : s
        );

        if (draft.allSensorDataForSingleNode.some((m) => m.Monitoring)) {
          draft.monitoringOn = true;
          break;
        }

        if (draft.allSensorDataForSingleNode.some((m) => !m.Monitoring)) {
          draft.monitoringOn = false;
          break;
        }
    }
  });
